/**
 * Thin alert-flavored wrapper around the shared AttachIntegrationTemplate —
 * see that file and assets/handoffs/design_handoff_attach_integration.
 * Kept as its own route (not folded into AlertPage.js, which is already
 * heavy and doesn't need to know about the "no integration" case).
 */
import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import AttachIntegrationTemplate from "./../templates/AttachIntegrationTemplate/AttachIntegrationTemplate";
import MajorToast from "../component/Toast/MajorToast";
import { store } from "../store";
import { alertSettingsValidator } from "../tools";
import { useAlertStore } from "./../../../hooks/redux/alert";
import { useIntegrationsStore } from "./../../../hooks/redux/integrations";

const NAVIGATE_DELAY_MS = 900;

const AttachAlertIntegrationPage = (props) => {
  const { alert, updateAlert, setAlert, fetchAlert, deleteAlert } = useAlertStore();
  const { integrations } = useIntegrationsStore();
  const history = useHistory();
  const uuid = props.match.params.uuid;

  const [alertModel, setAlertModel] = useState({
    uuid: "",
    name: "",
    available: 0,
    integration_uuid: null,
    template: "",
    show_private_events: 0,
    featured_events_limit: 0,
  });
  const [errors, setErrors] = useState({});
  const [attached, setAttached] = useState(false);

  useEffect(() => {
    fetchAlert(uuid);

    return () => setAlert(null);
  }, [uuid]);

  useEffect(() => {
    if (alert.fetchSuccess && alert.model) {
      setAlertModel({
        uuid: alert.model.uuid,
        name: alert.model.name,
        available: alert.model.available,
        integration_uuid: null,
        template: alert.model.template,
        show_private_events: alert.model.show_private_events,
        featured_events_limit: alert.model.featured_events_limit,
      });
    }
  }, [alert.fetchSuccess]);

  const onUpdateField = useCallback((fieldName, value) => {
    setAlertModel((previous) => {
      const updated = { ...previous, [fieldName]: value };
      setErrors(alertSettingsValidator(updated));
      return updated;
    });
  }, []);

  const onAttach = useCallback(async () => {
    await updateAlert(alertModel.uuid, alertModel);

    if (store.getState().alert.updateSuccess) {
      setAttached(true);
      setTimeout(() => history.push("/alert/" + alertModel.uuid), NAVIGATE_DELAY_MS);
    }
  }, [alertModel, updateAlert, history]);

  const onDelete = useCallback(
    async (e) => {
      e.preventDefault();

      if (alert.fetch || alert.delete) {
        return;
      }

      if (window.confirm("Do you really want to delete this widget? This action is irreversible.")) {
        await deleteAlert(uuid);
      }

      if (store.getState().alert.deleteSuccess) {
        history.push("/alerts");
      }
    },
    [alert.fetch, alert.delete, deleteAlert, uuid, history]
  );

  return (
    <>
      <AttachIntegrationTemplate
        entityNoun="alert"
        indexHref="/alerts"
        indexLabel="Event alerts"
        widgetModel={alertModel}
        untitled={!!alert.model && !alert.model.name}
        published={Boolean(alert.model?.available)}
        onUpdateField={onUpdateField}
        formErrors={errors}
        integrations={integrations.collection}
        attaching={alert.update}
        attachError={alert.updateError}
        onAttach={onAttach}
        onDelete={onDelete}
      />
      {attached && <MajorToast title="Integration attached" onDismiss={() => setAttached(false)} />}
    </>
  );
};

export default AttachAlertIntegrationPage;
